/**
 * Interactions as behavior graph data: an event on an object and the actions
 * it runs, compiled to native nodes with `GraphBuilder` (no scripts), and
 * appended to the graph the object already has. Nothing here changes the
 * scene; `interactionOps.ts` writes the result.
 *
 * A graph addresses its targets by paths relative to its object, worked out
 * when it is compiled, so compile against the hierarchy the graph will live in.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { EasingFunction, TimeTransition } from '../../scenedata/behaviors/propertiesdata';
import { GraphOps, type BehaviorGraphData, type GraphData } from '../../scenedata/behaviors/behaviorgraphdata';
import { GraphBuilder, type FlowChain } from '../../behaviors/behaviorgraph/GraphBuilder';
import { getBlockDef } from '../../behaviors/behaviorgraph/blockCatalog';
import { Properties } from '../../behaviors/properties';
import { isPlayable } from '../../behaviors/playable';
import { collectScopedStateTargets } from '../../core/canopy/stateScope';
import { resolveStateOwner } from '../states/statesActions';
import { validatePropertyValue } from './behaviorInput';
import { refuse } from './checks';
import type { Refusal } from './types';

/** Authorable triggers, each with the runtime op it opens and that op's catalog description. */
export const INTERACTION_EVENTS = ([
    { event: 'select', op: GraphOps.EVENT_ON_SELECT },
    { event: 'hover', op: GraphOps.EVENT_ON_HOVER_IN },
    { event: 'hoverExit', op: GraphOps.EVENT_ON_HOVER_OUT },
    { event: 'start', op: GraphOps.EVENT_ON_START },
    { event: 'tick', op: GraphOps.EVENT_ON_TICK },
    { event: 'proximity', op: GraphOps.TRACE_ON_PROXIMITY },
    { event: 'proximityExit', op: GraphOps.TRACE_ON_PROXIMITY },
    { event: 'stateChange', op: GraphOps.TRACE_ON_STATE_CHANGE },
] as const).map(trigger => ({
    ...trigger,
    description: getBlockDef(trigger.op)!.description,
}));

export type InteractionEvent = typeof INTERACTION_EVENTS[number]['event'];

/**
 * One action an interaction runs. A `state` of `''`, `'Base'` (any case) or
 * none returns to Base. `setGroupState` is `setState` on the group's owner
 * (`resolveStateOwner`), so the switch spans the group from any member.
 * `setProperty` writes the target's own declaration, a literal `value` or a
 * computed `expression` (exactly one), read in the interaction's scope.
 */
export type InteractionAction =
    | { action: 'setGroupState' | 'setState'; target: SceneObject; state?: string }
    | {
        action: 'setProperty'; target: SceneObject; property: string;
        value?: unknown; expression?: string; duration?: number; easing?: EasingFunction;
    }
    | { action: 'setActive'; target: SceneObject; active?: boolean }
    | { action: 'toggleActive' | 'playMedia' | 'pauseMedia'; target: SceneObject }
    | { action: 'setActiveChild'; target: SceneObject; index: number }
    | { action: 'playAnimation'; target: SceneObject; clip?: string; loop?: boolean }
    | { action: 'pagesNext' | 'pagesPrev'; target: SceneObject; wrap?: boolean; animate?: boolean }
    | { action: 'navigate'; url: string };

export interface Interaction {
    /** The object whose graph handles the event. */
    object: SceneObject;
    /** Default `select`. `hover` is the pointer entering, `hoverExit` leaving. */
    event?: InteractionEvent;
    /** Camera radius in metres; required only for `proximity` and `proximityExit`. */
    distance?: number;
    /** Where `when` and computed values read properties: it and its ancestors. Default `object`. */
    scope?: SceneObject;
    /** A boolean expression; the actions run only while it is true. */
    when?: string;
    /** Run in order; an action that animates or plays doesn't hold up the next. */
    actions: readonly InteractionAction[];
    /** `append` (default) adds the event to the object's graph; `replace` replaces the whole graph. */
    mode?: 'append' | 'replace';
}

/** What appending an interaction did to its object's graph, by event op (`event/onSelect`, …). */
export interface GraphUpdate {
    mode: 'append' | 'replace';
    beforeEvents: string[];
    afterEvents: string[];
    replaced: boolean;
}

const ACTIONS = [
    'setGroupState', 'setState', 'setProperty', 'setActive', 'toggleActive', 'setActiveChild',
    'playAnimation', 'playMedia', 'pauseMedia', 'pagesNext', 'pagesPrev', 'navigate',
] as const;
const EASINGS: readonly string[] = ['linear', 'easeIn', 'easeOut', 'easeInOut'];

/**
 * Throw unless `interaction` is well formed on its own: a known event, mode
 * and action for each entry, fields of the right type, and a target for
 * every action but `navigate`. Asks nothing of the scene.
 */
export function checkInteraction(interaction: Interaction): void {
    const event = interaction.event ?? 'select';
    if (!INTERACTION_EVENTS.some(trigger => trigger.event === event)) throw new Error(`unknown event "${event}"`);
    if (event === 'proximity' || event === 'proximityExit') {
        if (typeof interaction.distance !== 'number' || !Number.isFinite(interaction.distance) || interaction.distance < 0) {
            throw new Error('proximity distance must be finite and non-negative');
        }
    } else if (interaction.distance !== undefined) {
        throw new Error('distance is only valid for proximity events');
    }
    if (interaction.mode !== undefined && interaction.mode !== 'append' && interaction.mode !== 'replace') {
        throw new Error('mode must be append or replace');
    }
    if (interaction.when !== undefined && typeof interaction.when !== 'string') throw new Error('when must be a boolean expression string');
    const actions: unknown = interaction.actions;
    if (!Array.isArray(actions) || actions.length === 0) throw new Error('an interaction needs at least one action');
    for (const [index, action] of interaction.actions.entries()) {
        try {
            checkAction(action);
        } catch (error) {
            throw new Error(`action ${index + 1}: ${message(error)}`);
        }
    }
}

function checkAction(action: Record<string, unknown>): void {
    const name = action.action;
    for (const field of ['active', 'loop', 'wrap', 'animate']) {
        if (action[field] !== undefined && typeof action[field] !== 'boolean') throw new Error(`${field} must be boolean`);
    }
    for (const field of ['state', 'clip']) {
        if (action[field] !== undefined && typeof action[field] !== 'string') throw new Error(`${field} must be a string`);
    }
    if (!(ACTIONS as readonly unknown[]).includes(name)) {
        throw new Error(`unknown action "${String(name)}". Valid actions: ${ACTIONS.join(', ')}.`);
    }
    if (name !== 'navigate' && !action.target) throw new Error(`action "${String(name)}" requires a target`);
    switch (name) {
        case 'setProperty': {
            if (typeof action.property !== 'string' || !action.property) throw new Error('action "setProperty" requires a "property" name.');
            if ((action.value === undefined) === (action.expression === undefined)) throw new Error('setProperty requires exactly one of value or expression');
            if (action.expression !== undefined && typeof action.expression !== 'string') throw new Error('expression must be a string');
            const duration = action.duration;
            if (duration !== undefined && (typeof duration !== 'number' || !Number.isFinite(duration) || duration < 0)) throw new Error('duration must be finite and non-negative');
            if (action.easing !== undefined && !EASINGS.includes(action.easing as string)) throw new Error('Invalid easing');
            break;
        }
        case 'setActiveChild':
            if (typeof action.index !== 'number' || !Number.isInteger(action.index) || action.index < 0) {
                throw new Error('action "setActiveChild" requires a non-negative integer "index".');
            }
            break;
        case 'navigate':
            if (typeof action.url !== 'string' || !action.url) throw new Error('action "navigate" requires a "url".');
            break;
    }
}

/** An interaction compiled to a standalone graph, and a phrase for what each action does. */
export interface CompiledInteraction {
    graph: GraphData;
    summaries: string[];
}

/**
 * Compile an interaction to a graph for its object, against the scene as it
 * stands. Throws on an interaction {@link checkInteraction} rejects and on an
 * expression that doesn't compile (its compiler reports bad syntax and an
 * undeclared name alike). Refuses (`INVALID_WIRING`) an action the scene
 * can't carry out: a state the target's group doesn't declare, a property
 * the target doesn't declare or a value of another type, a child past the
 * last, media on an object that plays none, or state changes on an object
 * without Properties.
 */
export function compileInteraction(interaction: Interaction): CompiledInteraction | { refused: Refusal } {
    checkInteraction(interaction);
    return compileChecked(interaction);
}

/** {@link compileInteraction} for an interaction `checkInteraction` has passed. */
export function compileChecked(interaction: Interaction): CompiledInteraction | { refused: Refusal } {
    const { object } = interaction;
    if (interaction.event === 'stateChange' && !object.getBehavior(Properties)) {
        return { refused: refuse('INVALID_WIRING', `"${object.name}" has no Properties to observe for state changes`, object) };
    }
    const scope = interaction.scope ?? object;
    const builder = new GraphBuilder(object);
    let chain = openEvent(builder, interaction);
    if (interaction.when !== undefined) chain = chain.branch(builder.expression(interaction.when, scope, 'boolean').node).onTrue();
    const summaries: string[] = [];
    for (const [index, action] of interaction.actions.entries()) {
        let step: Step;
        try {
            step = addAction(builder, chain, action, scope);
        } catch (error) {
            throw new Error(`action ${index + 1}: ${message(error)}`);
        }
        if ('refused' in step) return { refused: { ...step.refused, message: `action ${index + 1}: ${step.refused.message}` } };
        chain = step.chain;
        summaries.push(step.summary);
    }
    return { graph: builder.build(), summaries };
}

function openEvent(builder: GraphBuilder, { object, event = 'select', distance }: Interaction): FlowChain {
    switch (event) {
        case 'select': return builder.onSelect();
        case 'start': return builder.onStart();
        case 'tick': return builder.onTick();
        // One node each: `onHover()` adds both the In and Out nodes, and the unused one stays in the graph unconnected.
        case 'hover': return builder.flowFrom(builder._addNode(GraphOps.EVENT_ON_HOVER_IN, {}), 'out');
        case 'hoverExit': return builder.flowFrom(builder._addNode(GraphOps.EVENT_ON_HOVER_OUT, {}), 'out');
        case 'proximity': return builder.onProximity(object, distance!).enter();
        case 'proximityExit': return builder.onProximity(object, distance!).exit();
        case 'stateChange': return builder.onStateChange(object);
    }
}

type Step = { chain: FlowChain; summary: string } | { refused: Refusal };

function addAction(builder: GraphBuilder, chain: FlowChain, action: InteractionAction, scope: SceneObject): Step {
    if (action.action === 'navigate') return { chain: chain.navigate(action.url), summary: `opens ${action.url}` };
    const { target } = action;
    const invalid = (text: string): Step => ({ refused: refuse('INVALID_WIRING', text, target) });
    switch (action.action) {
        case 'setGroupState': {
            const owner = resolveStateOwner(target);
            const props = owner.getBehavior(Properties);
            if (!props) return invalid(`"${owner.name}" (owner of "${target.name}") has no Properties/states.`);
            const state = stateName(action.state);
            if (state !== undefined && !props.getStateNames().includes(state)) {
                return invalid(`"${owner.name}" has no state "${state}". Available: ${props.getStateNames().join(', ') || '(none)'} (or "Base").`);
            }
            const members = collectScopedStateTargets(owner, state);
            return {
                chain: chain.setState(owner, state ?? ''),
                summary: `sets group state "${state ?? 'Base'}" on "${owner.name}" (scopes ${members.length} object${members.length === 1 ? '' : 's'} at runtime)`,
            };
        }
        case 'setState': {
            // Scoped at runtime: the switch spans the target's state group.
            const state = stateName(action.state);
            const declared = collectScopedStateTargets(target, state).some(object => {
                const props = object.getBehavior(Properties);
                return props && (state === undefined || props.getStateNames().includes(state));
            });
            if (!declared) return invalid(`No target in the scope of "${target.name}" declares state "${state ?? 'Base'}"`);
            return { chain: chain.setState(target, state ?? ''), summary: `sets state "${state ?? 'Base'}" on "${target.name}"` };
        }
        case 'setProperty': {
            // A write to the target's own declaration: unlike a read, it doesn't walk ancestors.
            const props = target.getBehavior(Properties);
            const names = props?.getPropertyNames() ?? [];
            if (!props || !names.includes(action.property)) {
                return invalid(`"${target.name}" has no declared property "${action.property}" on its own `
                    + `Properties behavior (setProperty does not walk ancestors — target the object `
                    + `that declares it). Available here: ${names.join(', ') || '(none)'}.`);
            }
            const type = props.getProperty(action.property)!.type;
            const transition: TimeTransition | undefined = action.duration === undefined ? undefined
                : { duration: action.duration, ...(action.easing ? { easing: action.easing } : {}) };
            let next: FlowChain;
            if (action.expression !== undefined) {
                if (type !== 'number' && type !== 'boolean' && type !== 'string') {
                    return invalid(`Computed assignments support number, boolean and string properties; received ${type}`);
                }
                const computed = builder.expression(action.expression, scope, type);
                next = chain.setPropertyFrom(target, action.property, computed.node, computed.socket, transition);
            } else {
                try {
                    validatePropertyValue(type, action.value);
                } catch (error) {
                    return invalid(message(error));
                }
                next = chain.setProperty(target, action.property, action.value, transition);
            }
            return {
                chain: next,
                summary: `sets property "${action.property}" = ${JSON.stringify(action.expression ?? action.value)} on "${target.name}"`
                    + (transition ? ` over ${transition.duration}s` : ''),
            };
        }
        case 'setActive': {
            const active = action.active !== false;
            return { chain: chain.setActive(target, active), summary: `${active ? 'shows' : 'hides'} "${target.name}"` };
        }
        case 'toggleActive':
            return { chain: chain.toggleActive(target), summary: `toggles "${target.name}" visible/hidden` };
        case 'setActiveChild':
            if (action.index >= target.getChildren().length) {
                return invalid(`"${target.name}" has ${target.getChildren().length} children, so it has no child ${action.index}`);
            }
            return { chain: chain.setActiveChild(target, action.index), summary: `activates child ${action.index} of "${target.name}"` };
        case 'playAnimation':
            return {
                // Continue from the start, not the end: the next action doesn't wait for the clip.
                chain: chain.playAnimation(target, action.clip, { loop: action.loop === true }).then,
                summary: `plays animation${action.clip ? ` "${action.clip}"` : ''} on "${target.name}"`,
            };
        case 'playMedia':
            if (!target.getBehaviors().some(isPlayable)) return invalid(`"${target.name}" has no Playable behavior; Synth requires note/tone commands and cannot use playMedia`);
            return { chain: chain.playMedia(target), summary: `plays media on "${target.name}"` };
        case 'pauseMedia':
            if (!target.getBehaviors().some(isPlayable)) return invalid(`"${target.name}" has no Playable behavior`);
            return { chain: chain.pauseMedia(target), summary: `pauses media on "${target.name}"` };
        case 'pagesNext':
            return { chain: chain.pagesNext(target, action.wrap !== false, action.animate === true), summary: `advances "${target.name}" to the next page` };
        case 'pagesPrev':
            return { chain: chain.pagesPrev(target, action.wrap !== false, action.animate === true), summary: `moves "${target.name}" to the previous page` };
    }
}

/** The state an action names, or undefined for Base. */
function stateName(state: string | undefined): string | undefined {
    return state == null || state === '' || state.toLowerCase() === 'base' ? undefined : state;
}

/**
 * Add a compiled interaction to `existing` (an object's graph data, or none),
 * without renumbering what is there. `append` adds the interaction to the
 * active graph, keeping every other graph, variable, event and node; an event
 * the active graph already handles comes back as `conflicts`, and the data is
 * left as it was. `replace` returns a graph holding only the interaction.
 *
 * Throws on an `existing` whose `activeGraph` names no graph, and on an
 * interaction that isn't standalone (variables, custom events or subgraph
 * sockets), which `append` can't merge.
 */
export function composeInteraction(
    existing: BehaviorGraphData | undefined,
    interaction: GraphData,
    mode: 'append' | 'replace' = 'append',
): { data: BehaviorGraphData; update: GraphUpdate } | { conflicts: string[]; existingEvents: string[] } {
    if (mode !== 'append' && mode !== 'replace') throw new Error('mode must be append or replace');
    const active = existing?.activeGraph ?? 0;
    const base = existing?.graphs ? existing.graphs[active] : existing?.graph;
    if (existing?.graphs && !base) throw new Error('Existing activeGraph index is invalid; graph left unchanged');
    const beforeEvents = base ? connectedEvents(base) : [];
    const incomingEvents = connectedEvents(interaction);
    const describe = (data: BehaviorGraphData, afterEvents: string[]) => ({ data, update: { mode, beforeEvents, afterEvents, replaced: !!base && mode === 'replace' } });
    if (!base || mode === 'replace') return describe({ '@type': 'BehaviorGraph', graph: structuredClone(interaction), graphs: undefined, activeGraph: 0 }, incomingEvents);
    const conflicts = beforeEvents.filter(event => incomingEvents.includes(event));
    if (conflicts.length) return { conflicts, existingEvents: beforeEvents };
    if (interaction.variables.length || interaction.events.length || interaction.inputFlowSockets || interaction.outputFlowSockets || interaction.inputValueSockets || interaction.outputValueSockets || interaction.declarations.some(d => d.extensions)) {
        throw new Error('Append accepts a standalone interaction without variables, custom events or subgraph interfaces');
    }
    const graph = structuredClone(base);
    const types = interaction.types.map(type => {
        let index = graph.types.findIndex(t => t.signature === type.signature);
        if (index < 0) { index = graph.types.length; graph.types.push(structuredClone(type)); }
        return index;
    });
    const declarationOffset = graph.declarations.length, nodeOffset = graph.nodes.length;
    graph.declarations.push(...structuredClone(interaction.declarations));
    for (const source of interaction.nodes) {
        const node = structuredClone(source);
        node.declaration += declarationOffset;
        // A configuration value addresses something (a target path, a name), so
        // its type index may fall back to the first type.
        for (const value of Object.values(node.configuration ?? {})) value.type = types[value.type] ?? 0;
        for (const value of Object.values(node.values ?? {})) {
            if ('node' in value) value.node += nodeOffset;
            else {
                const type = types[value.type];
                if (type === undefined) throw new Error('Interaction contains an invalid literal type index');
                value.type = type;
            }
        }
        for (const flow of Object.values(node.flows ?? {})) flow.node += nodeOffset;
        if (node.metadata && typeof node.metadata.x === 'number') node.metadata.x += nodeOffset * 250;
        graph.nodes.push(node);
    }
    const data = structuredClone(existing!);
    if (data.graphs) data.graphs[active] = graph;
    else data.graph = graph;
    return describe(data, connectedEvents(graph));
}

/** The event ops in `graph` that start a flow; proximity enter and exit are separate triggers, named `<op>.enter` and `<op>.exit`. */
function connectedEvents(graph: GraphData): string[] {
    return [...new Set(graph.nodes.flatMap(node => {
        const op = graph.declarations[node.declaration]?.op;
        if (!op?.startsWith('event/') && !op?.startsWith('trace/on')) return [];
        const outputs = Object.keys(node.flows ?? {});
        return op === GraphOps.TRACE_ON_PROXIMITY ? outputs.map(output => `${op}.${output}`) : outputs.length ? [op] : [];
    }))].sort();
}

function message(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}
